import { GetContractsResponse, PensionPhaseStatus, ProductType } from "@repo/api";
import { useMemo } from "react";
import { isAgeInRange } from "./dateUtils";

const DPS_MIGRATION_MIN_AGE = 18;
const DPS_MIGRATION_MAX_AGE = 55;

export const shouldShowDpsMigrationBanner = (contracts: GetContractsResponse[], birthDate?: Date | null): boolean => {
    // check if user has "Penzijni pripojisteni" (PF) contracts
    const hasPF = contracts.some((contract) => contract.type === ProductType.Pf);

    // check if user does NOT have adult "Doplnkove penzijni sporeni" (UF) contracts && and also ignore children contracts
    const hasNoUF = !contracts.some((contract) => contract.type === ProductType.Uf && !contract.isChildrenContract);

    // check if user is within age range 18-55
    const isWithinAgeRange = isAgeInRange(birthDate, DPS_MIGRATION_MIN_AGE, DPS_MIGRATION_MAX_AGE);

    // show only if the user has PF but no adult UF contracts AND is aged 18-55
    return hasPF && hasNoUF && isWithinAgeRange;
};

export const shouldShowPensionBanner = (contracts: GetContractsResponse[]): boolean => {
    // zivotka
    const lifeContractsCount = contracts.filter((x) => x.type === ProductType.Cl).length;

    // penze detske
    const childrenContractCount = contracts.filter((x) => x.isChildrenContract).length;

    // penze total
    const savingsContractsCount = contracts.filter((x) =>
        ([ProductType.Pf, ProductType.Uf] as ProductType[]).includes(x.type),
    ).length;

    // penze bez detskych
    const adultSavingsContractsCount = savingsContractsCount - childrenContractCount;

    // zadani: https://www.figma.com/design/Li3vIFcoKWjoVayUvbAaFt?node-id=22846-18683#1235871024
    // podminka pro zobrazeni:
    // pouze u klientu ktery maji L400 smlouvy
    // a muzou mit detske DPS ale ne jine
    return lifeContractsCount > 0 && adultSavingsContractsCount === 0;
};

// Only return UF (DPS) contract in saving phase, not PF
export const findPensionNovelContract = (contracts: GetContractsResponse[]): GetContractsResponse | undefined => {
    return contracts.find(
        (x) => x.pensionPhaseStatus === PensionPhaseStatus.Saving && !x.isChildrenContract && x.type === ProductType.Uf,
    );
};

export const possibleTaxSavingFormulaAfterNovel = (monthlyContribution: number): number => {
    return Math.round(Math.max(Math.min(monthlyContribution - 1700, 4000), 0) * 12 * 0.15); // formula acquired from business department
};

export const usePossibleTaxSavingFormulaAfterNovel = (monthlyContribution: number): number => {
    return useMemo(() => possibleTaxSavingFormulaAfterNovel(monthlyContribution), [monthlyContribution]);
};

export const yearlyStateContributionFormulaAfterNovel = (monthlyContribution: number): number => {
    // formula acquired from business department
    if (monthlyContribution < 500) {
        return 0;
    } else if (monthlyContribution > 499 && monthlyContribution < 1700) {
        const monthlyContributionAbove500 = monthlyContribution - 500;
        return Math.round(100 + 0.2 * monthlyContributionAbove500) * 12;
    } else return 340 * 12;
};

export const useYearlyStateContributionFormulaAfterNovel = (monthlyContribution: number): number => {
    return useMemo(() => yearlyStateContributionFormulaAfterNovel(monthlyContribution), [monthlyContribution]);
};
